import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ParseKeys } from 'i18next';

import { CheckDurationTime } from 'Src/feature/check/CheckDurationTime';
import { CheckFromTime } from 'Src/feature/check/CheckFromTime';
import { CheckDef } from 'Src/feature/check/models';
import { useCheckList } from 'Src/feature/check-list/useCheckList';
import { useChecksPage } from 'Src/feature/checks/useChecksPage';
import { FormField } from 'Src/feature/form/FormField';
import { FormFieldSelectProps } from 'Src/feature/form/FormFieldSelect';
import { formatMonthDayOr12hourTime } from 'Src/utils/date';

import styles from 'Src/feature/checks/ChecksList.module.scss';

const sharedSize: FormFieldSelectProps['size'] = 'sm';

type UseComponentsFiltersOptions = {
  store: ReturnType<typeof useCheckList>;
}
const useComponents_filters = (options: UseComponentsFiltersOptions) => {
  const checksPage = useChecksPage();
  const { store } = options;

  const filter_revenueCenterId = useMemo(() => (
    <FormField
      key="revCenterFilter"
      type="select"
      variant="outlined"
      size={sharedSize}
      multiple
      value={store.currentValues.revenueCenterId}
      options={store.options.revenueCenterId}
      onChange={(_evt, values) => store.filterHandlers.revenueCenterId(values as (string | number)[])}
      renderValue={store.renderOverrides.revenueCenterId}
    />
  ), [store.currentValues.revenueCenterId, store.filterHandlers, store.options.revenueCenterId, store.renderOverrides.revenueCenterId]);

  const filter_orderTypeId = useMemo(() => (
    <FormField
      key="orderTypeIdFilter"
      type="select"
      variant="outlined"
      size={sharedSize}
      multiple
      value={store.currentValues.orderTypeId}
      options={store.options.orderTypeId}
      onChange={(_evt, values) => store.filterHandlers.orderTypeId(values as (string | number)[])}
      renderValue={store.renderOverrides.orderTypeId}
    />
  ), [store.currentValues.orderTypeId, store.filterHandlers, store.options.orderTypeId, store.renderOverrides.orderTypeId]);

  const filter_status = useMemo(() => (
    <FormField
      key="statusFilter"
      type="select"
      variant="outlined"
      size={sharedSize}
      multiple
      value={store.currentValues.status}
      options={store.options.status}
      onChange={(_evt, values) => store.filterHandlers.status(values as (string | number)[])}
      renderValue={store.renderOverrides.status}
    />
  ), [store.currentValues.status, store.filterHandlers, store.options.status, store.renderOverrides.status]);

  const filter_employeeId = useMemo(() => (
    <FormField
      key="employeeIdFilter"
      type="select"
      variant="outlined"
      size={sharedSize}
      multiple
      value={store.currentValues.employeeId}
      options={store.options.employeeId}
      onChange={(_evt, values) => store.filterHandlers.employeeId(values as (string | number)[])}
      renderValue={store.renderOverrides.employeeId}
    />
  ), [store.currentValues.employeeId, store.filterHandlers, store.options.employeeId, store.renderOverrides.employeeId]);

  const filter_orderChannelId = useMemo(() => (
    <FormField
      key="orderChannelIdFilter"
      type="select"
      variant="outlined"
      size={sharedSize}
      multiple
      value={store.currentValues.orderChannelId}
      options={store.options.orderChannelId}
      onChange={(evt, values) => {
        store.filterHandlers.orderChannelId(values as (string | number)[]);
      }}
      renderValue={store.renderOverrides.orderChannelId}
    />
  ), [store.currentValues.orderChannelId, store.filterHandlers, store.options.orderChannelId, store.renderOverrides.orderChannelId]);

  if (checksPage.currentSelection === 'current') {
    return [
      filter_revenueCenterId,
      filter_orderTypeId,
      filter_status,
      filter_employeeId,
    ];
  }

  if (checksPage.currentSelection === 'scheduled') {
    return [
      filter_revenueCenterId,
      filter_orderTypeId,
      filter_orderChannelId
    ];
  }

  if (checksPage.currentSelection === 'searchMode') {
    return [
      filter_revenueCenterId,
      filter_orderTypeId,
      filter_orderChannelId
    ];
  }

  return [];
};


const getStatusRowStyle = (dataItem: { status: CheckDef['status'] }) => {

  const style_status = () => {
    switch (dataItem.status) {
      case 'Opened': return styles.table_cell_open;
      case 'Scheduled': return styles.table_cell_warn;
      case 'MergeVoided':
      case 'Voided': return styles.table_cell_error;
      case 'Closed': return styles.table_cell_disable_look;
      default: return undefined;
    }
  };

  const style_total = () => {
    switch (dataItem.status) {
      case 'MergeVoided':
      case 'Voided': return styles.table_cell_error_strike;
      case 'Closed': return styles.table_cell_disable_look;
      default: return undefined;
    }
  };

  const style_common = () => {
    switch (dataItem.status) {
      case 'MergeVoided':
      case 'Voided':
      case 'Closed': return styles.table_cell_disable_look;
      default: return undefined;
    }
  };

  return {
    status: style_status(),
    total: style_total(),
    common: style_common(),
  };
};


type UseComponentsColumnsOptions = {
  store: ReturnType<typeof useCheckList>;
}
const useComponents_columns = (options: UseComponentsColumnsOptions) => {

  const { t } = useTranslation();
  const { store } = options;
  const { Column } = store.table;
  const checksPage = useChecksPage();

  const column_label = useMemo(() => (
    <Column
      key="labelCol"
      label={t('labels.labelSlashGuest')}
      field="label"
      sortable
      minWidth="10rem"
      className={({ dataItem }) => `${getStatusRowStyle(dataItem).common} ${styles.table_label_col}`}
      cellContentRenderer={({ dataItem }) => (
        <>
          {dataItem.hasIndefiniteHoldItems && <div className={styles.corner_triangle} />}
          {dataItem.label}
        </>
      )}
    />
  ), [Column, t]);

  const column_status = useMemo(() => (
    <Column
      key="statusCol"
      label={t('labels.status')}
      field="status"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).status}
      cellContentRenderer={({ value }) => t(`check.labels.tableListOverrides.status.${value}` as ParseKeys<'translation'>)}
    />
  ), [Column, t]);

  const column_orderChannel = useMemo(() => (
    <Column
      key="orderChannelCol"
      label={t('labels.channel')}
      field="orderChannel"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
    />
  ), [Column, t]);

  const column_tableName = useMemo(() => (
    <Column
      key="tableNameCol"
      label={t('labels.table')}
      field="tableName"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
    />
  ), [Column, t]);

  const column_revCenter = useMemo(() => (
    <Column
      key="revCenterCol"
      label={t('revenueCenter.shortLabel')}
      field="revCenter"
      sortable
      minWidth="25rem"
      cellContentRenderer={({ dataItem }) => dataItem.revCenter ? dataItem.revCenter : t('revenueCenter.labels.unknown')}
    />
  ), [Column, t]);

  const column_orderType = useMemo(() => (
    <Column
      key="orderTypeCol"
      label={t('labels.type')}
      field="orderType"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
      cellContentRenderer={({ dataItem }) => dataItem.orderType ? dataItem.orderType : t('orderType.labelUnknown')}
    />
  ), [Column, t]);

  const column_checkNumber = useMemo(() => (
    <Column
      key="checkNumberCol"
      label={t('check.labels.checkNumber')}
      field="number"
      sortable
      minWidth="13rem"
      width="16rem"
    />
  ), [Column, t]);

  const column_total = useMemo(() => (
    <Column
      key="totalCol"
      label={t('check.labels.balance.total')}
      field="total"
      sortable
      align="right"
      minWidth="10rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).total}
      cellContentRenderer={({ dataItem }) => t('labels.util.currency', { value: dataItem.total })}
    />
  ), [Column, t]);

  const column_timeOpen = useMemo(() => (
    <Column
      key="timeOpenCol"
      label={t('labels.timeOpen')}
      field="timeOpen"
      sortable
      minWidth="10rem"
      width="14rem"
      cellContentRenderer={({ dataItem }) => {
        if (dataItem.status === 'Closed') {
          return (
            <CheckDurationTime checkCreatedTimeMilli={dataItem.openedAt} checkClosedTimeMilli={dataItem.closedAt} />
          );
        }
        return (
          <CheckFromTime timeMilli={dataItem.openedAt} />
        );
      }}
    />
  ), [Column, t]);

  const column_createdAt = useMemo(() => (
    <Column
      key="createdAtCol"
      label={t('labels.created')}
      field="createdAt"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
      cellContentRenderer={({ dataItem }) => formatMonthDayOr12hourTime(new Date(dataItem.createdAt))}
    />
  ), [Column, t]);

  const column_partySize = useMemo(() => (
    <Column
      key="partySizeCol"
      label={t('labels.cover_plural')}
      field="partySize"
      sortable
      align="right"
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
    />
  ), [Column, t]);

  const column_beginPrepAt = useMemo(() => (
    <Column
      key="beginPrepAtCol"
      label={t('labels.prepAt')}
      field="beginPrepAt"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
      cellContentRenderer={({ dataItem }) => formatMonthDayOr12hourTime(new Date(dataItem.beginPrepAt))}
    />
  ), [Column, t]);

  const column_employeeShortName = useMemo(() => (
    <Column
      key="employeeShortNameCol"
      label={t('labels.owner')}
      field="employeeShortName"
      sortable
      minWidth="10rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
    />
  ), [Column, t]);

  const column_scheduledAt = useMemo(() => (
    <Column
      key="scheduledAtCol"
      label={t('labels.scheduled')}
      field="scheduledAt"
      sortable
      minWidth="10rem"
      width="16rem"
      className={({ dataItem }) => getStatusRowStyle(dataItem).common}
      cellContentRenderer={({ dataItem }) => dataItem.scheduledAt ? formatMonthDayOr12hourTime(new Date(dataItem.scheduledAt)) : t('labels.scheduledUnknown')}
    />
  ), [Column, t]);

  if (checksPage.currentSelection === 'current') {
    return [
      column_label,
      column_status,
      column_tableName,
      column_revCenter,
      column_checkNumber,
      column_timeOpen,
      column_partySize,
      column_employeeShortName,
      column_total,
    ];
  }

  if (checksPage.currentSelection === 'scheduled') {
    return [
      column_label,
      column_orderChannel,
      column_revCenter,
      column_orderType,
      column_checkNumber,
      column_createdAt,
      column_beginPrepAt,
      column_scheduledAt,
      column_total,
    ];
  }

  if (checksPage.currentSelection === 'searchMode') {
    return [
      column_label,
      column_status,
      column_tableName,
      column_revCenter,
      column_orderChannel,
      column_orderType,
      column_checkNumber,
      column_partySize,
      column_employeeShortName,
      column_total,
    ];
  }

  return [];
};

const useCheckListComponents = {
  filters: useComponents_filters,
  columns: useComponents_columns,
};

export { useCheckListComponents };
